// ASC 718 / ASC 820 alignment annex — PSD §9.7 (F9.7). State layer.

import type { ValuationResult } from '@backsolve/shared-types';

export interface AscAnnex {
  asc718: {
    standard: string;
    classification: string;
    grantDateFairValue: number;
    measurementApproach: string;
    disclosure: string;
  };
  asc820: {
    level: 'Level 1' | 'Level 2' | 'Level 3';
    inputs: { name: string; value: string; level: 1 | 2 | 3 }[];
    disclosure: string;
  };
}

/**
 * Build an ASC alignment annex from a valuation result.
 */
export function buildAscAnnex(result: ValuationResult): AscAnnex {
  const h = result.headline;
  const a = result.inputs.assumptions;

  return {
    asc718: {
      standard: 'ASC 718 — Compensation — Stock Compensation',
      classification: result.inputs.compliance.grantDate
        ? `Grant date: ${result.inputs.compliance.grantDate}`
        : 'Grant date: (to be determined)',
      grantDateFairValue: h.commonFmvPerShare,
      measurementApproach:
        'Fair value of underlying common stock at grant date using backsolve OPM from recent preferred transaction.',
      disclosure:
        'The fair value of the underlying common stock was determined using a backsolve OPM approach. ' +
        'The model assumes a risk-free rate, volatility, and time to liquidity as documented in the assumption memo.',
    },
    asc820: {
      level: 'Level 3',
      inputs: [
        { name: 'Volatility', value: `${(a.volatility * 100).toFixed(1)}%`, level: 3 },
        { name: 'Time to Liquidity', value: `${a.timeToLiquidity} years`, level: 3 },
        { name: 'Risk-Free Rate', value: `${(a.riskFreeRate * 100).toFixed(2)}%`, level: 2 },
        { name: 'Dividend Yield', value: `${(a.dividendYield * 100).toFixed(2)}%`, level: 3 },
        { name: 'DLOM', value: `${(result.inputs.discounts.dlomPercentage * 100).toFixed(1)}%`, level: 3 },
        { name: 'Total Equity Value', value: `$${h.totalEquityValue.toLocaleString()}`, level: 3 },
      ],
      disclosure:
        'The fair value measurement is classified as Level 3 in the fair value hierarchy. ' +
        'Key unobservable inputs include volatility, time to liquidity, and DLOM. ' +
        'Significant increases in volatility or decreases in DLOM would result in higher fair value.',
    },
  };
}
